import {
  type ColumnFiltersState,
  createColumnHelper,
  type PaginationState,
  type VisibilityState,
} from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import {
  type StudentWithMedical,
  useGetClassMedicalDetails,
} from '~entities/classes/api/get-class-medical-details';

import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';

export type { StudentWithMedical } from '~entities/classes/api/get-class-medical-details';

const columnHelper = createColumnHelper<StudentWithMedical>();

const medicalColumns = [
  columnHelper.accessor('name', {
    header: (info) => (
      <DataTableColumnHeader disableSorting column={info.column} title='Name' />
    ),
    cell: (info) => <div>{info.getValue()}</div>,
  }),
  columnHelper.accessor((row) => row.medical.primaryDoctor, {
    id: 'primaryDoctor',
    header: (info) => (
      <DataTableColumnHeader
        disableSorting
        column={info.column}
        title='Primary Doctor'
      />
    ),
    cell: (info) => <div>{info.getValue() || '—'}</div>,
  }),
  columnHelper.accessor((row) => row.medical.medications, {
    id: 'medications',
    header: (info) => (
      <DataTableColumnHeader
        disableSorting
        column={info.column}
        title='Medications'
      />
    ),
    cell: (info) => <div>{info.getValue() || '—'}</div>,
  }),
  columnHelper.accessor((row) => row.medical.allergies, {
    id: 'allergies',
    header: (info) => (
      <DataTableColumnHeader
        disableSorting
        column={info.column}
        title='Allergies'
      />
    ),
    cell: (info) => <div>{info.getValue() || '—'}</div>,
  }),
  columnHelper.accessor((row) => row.medical.disabilities, {
    id: 'disabilities',
    header: (info) => (
      <DataTableColumnHeader
        disableSorting
        column={info.column}
        title='Disabilities'
      />
    ),
    cell: (info) => <div>{info.getValue() || '—'}</div>,
  }),
  columnHelper.accessor((row) => row.medical.specialNeeds, {
    id: 'specialNeeds',
    header: (info) => (
      <DataTableColumnHeader
        disableSorting
        column={info.column}
        title='Special Needs'
      />
    ),
    cell: (info) => <div>{info.getValue() || '—'}</div>,
  }),
  columnHelper.accessor((row) => row.medical.healthInsuranceCarrier, {
    id: 'healthInsuranceCarrier',
    header: (info) => (
      <DataTableColumnHeader
        disableSorting
        column={info.column}
        title='Health Insurer'
      />
    ),
    cell: (info) => <div>{info.getValue() || '—'}</div>,
  }),
];

export interface MedicalDetailsCardProps {
  /** Class ID (enrollment ID) to fetch medical details for */
  classId: string;
  /** Optional className for the card (e.g. mt-6 or md:col-span-3) */
  className?: string;
}

export const MedicalDetailsCard = ({
  classId,
  className,
}: MedicalDetailsCardProps) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const { data, isLoading, isError } = useGetClassMedicalDetails({
    classId,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
  });

  return (
    <Card className={className}>
      <CardHeader className='p-2 sm:p-6'>
        <CardTitle>Medical Details</CardTitle>
        <CardDescription>
          Students with medical information in this class
        </CardDescription>
      </CardHeader>
      <CardContent className='p-2 pt-0 sm:p-6 sm:pt-0'>
        {isLoading ? (
          <div className='flex h-32 items-center justify-center'>
            <Loader2 className='size-8 animate-spin text-muted-foreground' />
          </div>
        ) : isError ? (
          <p className='py-4 text-center text-sm text-destructive'>
            Failed to load medical details.
          </p>
        ) : (
          <EnrollioTable
            ariaLabel='Student medical details'
            totalLabel='Students with medical details'
            filtersLayout='inline'
            filterHeader={(table) => (
              <EnrollioTableSearch
                value={
                  (table.getColumn('name')?.getFilterValue() as
                    | string
                    | undefined) ?? ''
                }
                onChange={(value) => {
                  table.getColumn('name')?.setFilterValue(value);
                }}
                placeholder='Search student name...'
              />
            )}
            toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
            columns={medicalColumns}
            data={data?.items ?? []}
            rowCount={data?.pagination.totalItems ?? 0}
            manualPagination
            pagination={pagination}
            onPaginationChange={setPagination}
            columnFilters={columnFilters}
            onColumnFiltersChange={setColumnFilters}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            isUpdating={isLoading}
          />
        )}
      </CardContent>
    </Card>
  );
};
